import {
  Menu as AriakitMenu,
  MenuButton as AriakitMenuButton,
  MenuButtonProps as AriakitMenuButtonProps,
  MenuItem as AriakitMenuItem,
  MenuItemProps as AriakitMenuItemProps,
  MenuProps as AriakitMenuProps,
  MenuSeparator as AriakitMenuSeparator,
  MenuSeparatorProps as AriakitMenuSeparatorProps,
} from "@ariakit/react";
import { clsx } from "clsx";
import * as React from "react";
import { forwardRef } from "react";

import { PopoverCard } from "./Popover";

export { useMenuStore } from "@ariakit/react";

export interface MenuButtonProps extends AriakitMenuButtonProps {
  /**
   * Use the child as the component.
   */
  asChild?: boolean;
  children?: React.ReactElement;
}

export const MenuButton = forwardRef<HTMLDivElement, MenuButtonProps>(
  ({ asChild, children, ...props }, ref) => {
    return (
      <AriakitMenuButton
        ref={ref}
        render={asChild ? children : <AriakitMenuButton />}
        {...props}
      >
        {asChild ? null : children}
      </AriakitMenuButton>
    );
  },
);

if (process.env["NODE_ENV"] !== "production") {
  MenuButton.displayName = "MenuButton";
}

export type MenuProps = AriakitMenuProps & {
  /**
   * Whether the menu contains a combobox.
   * @default false
   */
  combobox?: boolean;
  gutter?: number;
};

export const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ className, style, combobox, gutter = 4, ...props }, ref) => {
    return (
      <AriakitMenu
        ref={ref}
        focusable={false}
        render={<PopoverCard />}
        gutter={gutter}
        className={clsx(
          className,
          combobox ? "flex flex-col overflow-hidden" : "overflow-auto p-1",
        )}
        composite={!combobox}
        style={{
          maxHeight: "var(--popover-available-height, 400px)",
          ...style,
        }}
        {...props}
      />
    );
  },
);

if (process.env["NODE_ENV"] !== "production") {
  Menu.displayName = "Menu";
}

export type MenuItemScale = "sm" | "md";
export type MenuItemVariant = "default" | "danger" | "success";

const menuItemVariants: Record<MenuItemVariant, string> = {
  default: clsx(
    "data-[active-item]:bg-grey-bg-hover-transparent data-[active-item]:text-text-on-hover",
    "aria-disabled:opacity-disabled",
  ),
  danger: clsx(
    "text-danger-on",
    "data-[active-item]:bg-danger-bg-hover-transparent data-[active-item]:text-danger-on-hover",
    "aria-disabled:opacity-disabled",
  ),
  success: clsx(
    "text-success-on",
    "data-[active-item]:bg-success-bg-hover-transparent data-[active-item]:text-success-on-hover",
    "aria-disabled:opacity-disabled",
  ),
};

const menuItemScales: Record<MenuItemScale, string> = {
  md: "text-base py-1 px-2",
  sm: "text-sm py-1 px-2",
};

export interface ItemClassNameProps {
  /**
   * Variant of the menu item.
   * @default "default"
   */
  variant?: MenuItemVariant | undefined;
  scale?: MenuItemScale | undefined;

  className?: string | undefined;
}

export const getItemClassName = (props: ItemClassNameProps) => {
  const { variant = "default", scale = "md", className } = props;

  return clsx(
    className,
    "rounded font-accent flex items-center gap-2 focus:outline-none",
    menuItemVariants[variant],
    menuItemScales[scale],
  );
};

export interface MenuItemProps
  extends AriakitMenuItemProps,
    ItemClassNameProps {
  /**
   * Use the child as the component.
   */
  asChild?: boolean;
  children?: React.ReactNode;
}

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  ({ asChild, children, className, variant, scale, ...props }, ref) => {
    return (
      <AriakitMenuItem
        ref={ref}
        className={getItemClassName({
          className,
          variant,
          scale,
        })}
        render={
          asChild ? (children as React.ReactElement) : <AriakitMenuItem />
        }
        {...props}
      >
        {asChild ? null : children}
      </AriakitMenuItem>
    );
  },
);

if (process.env["NODE_ENV"] !== "production") {
  MenuItem.displayName = "MenuItem";
}

export interface ListSeparatorClassNameProps {
  className?: string | undefined;
}

export const getListSeparatorClassName = ({
  className,
}: ListSeparatorClassNameProps) => {
  return clsx(className, "border-t border-t-grey-border-light my-1 mx-2");
};

export type MenuSeparatorProps = AriakitMenuSeparatorProps &
  ListSeparatorClassNameProps;

export const MenuSeparator = forwardRef<HTMLHRElement, MenuSeparatorProps>(
  ({ className, ...props }, ref) => {
    const listSeparatorClassName = getListSeparatorClassName({ className });
    return (
      <AriakitMenuSeparator
        ref={ref}
        className={listSeparatorClassName}
        {...props}
      />
    );
  },
);

if (process.env["NODE_ENV"] !== "production") {
  MenuSeparator.displayName = "MenuSeparator";
}
